import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { GoogleOAuthProvider } from '@react-oauth/google'
// Self-hosted fonts (offline-safe) — weights mirror the former Google Fonts request.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/atkinson-hyperlegible/400.css';
import '@fontsource/atkinson-hyperlegible/700.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/700.css';
import AuthGate from './components/Auth/AuthGate.jsx'
import PublicGate from './components/Public/PublicGate.jsx'
import GuestGate from './components/Public/GuestGate.jsx'
import { VerifyEmailPage, ResetPasswordPage } from './components/Auth/AuthPages.jsx'
import { ThemeProvider, applyTheme, applyFontScale, applyFont, THEMES } from './contexts/ThemeContext.jsx'
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary.jsx'
import { installDiagnosticsBuffer } from './utils/diagnosticsBuffer.js'

// Ring buffer of recent errors / failed API calls, attached to issue reports.
installDiagnosticsBuffer();

// One-time cleanup: the opt-in video prefetch cache was retired in favour of the
// VTR ABR ladder. Unregister the old Service Worker and drop its cache so browsers
// that registered it during the beta clean themselves up. (Safe to delete this
// block once the beta audience has reloaded at least once.)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations?.().then(regs => {
    regs.forEach(reg => {
      const url = reg.active?.scriptURL || reg.waiting?.scriptURL || reg.installing?.scriptURL || '';
      if (url.endsWith('/sw.js')) reg.unregister().catch(() => {});
    });
  }).catch(() => {});
}
if ('caches' in window) {
  caches.delete('trackvideo-v1').catch(() => {});
}

// Apply saved appearance before React renders to prevent a flash of unstyled content
const savedTheme = localStorage.getItem('trackapp:theme');
applyTheme(savedTheme && THEMES[savedTheme] ? savedTheme : 'default');
applyFontScale(localStorage.getItem('trackapp:fontScale'));
applyFont(localStorage.getItem('trackapp:fontFamily'));

const path = window.location.pathname;
const normalizedPath = path.toLowerCase();

let rootElement;
if (path === '/verify-email') {
  rootElement = <VerifyEmailPage />;
} else if (path === '/reset-password') {
  rootElement = <ResetPasswordPage />;
} else if (path.startsWith('/s/')) {
  // Account-free public share link: /s/<token>
  const token = decodeURIComponent(path.slice('/s/'.length));
  rootElement = <PublicGate token={token} />;
} else if (path.startsWith('/tracks/') || path === '/tracks') {
  // Public track maps. In production these are prerendered static files served
  // directly by Caddy (try_files reaches dist/tracks/<slug>.html) and this
  // branch never runs — it exists so the path cannot fall through to the login
  // wall, and so `vite dev` can reach the pages at all. The SPA has no
  // track-page view to render, so hand off to the real entry, carrying the slug
  // since the redirect destroys the path it was in.
  const slug = path.replace(/^\/tracks\/?/, '').replace(/\.html$/, '');
  window.location.replace(slug ? `/track.html?slug=${encodeURIComponent(slug)}` : '/track.html');
  rootElement = null;
} else if (path === '/try' || path.startsWith('/try')) {
  // No-signup guest sandbox (upload + analyze without an account)
  rootElement = <GuestGate />;
} else if (normalizedPath === '/uploadanything' || normalizedPath === '/uploadanything/') {
  rootElement = <AuthGate route="upload-anything" />;
} else if (normalizedPath === '/desktop-pair' || normalizedPath === '/desktop-pair/') {
  rootElement = <AuthGate route="desktop-pair" />;
} else {
  rootElement = <AuthGate />;
}

// Google client id is optional — when unset, GoogleOAuthProvider is still safe
// to mount and the login page simply hides the "Continue with Google" button.
const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={googleClientId}>
        <ThemeProvider>
          {rootElement}
        </ThemeProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  </StrictMode>,
)
